import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/tracker/v1/tracker.types';
import {DropType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/drop.types';
import {Flyout_ImageContainer_FlyoutSection_FlyoutSectionItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {TrackImageTeaser} from '@aboutyou/tracking';
import {UnstyledLinkLinaria} from '@aboutyou/ui-components';
import React from 'react';
import {useTrackFlyoutOriginClick} from '../../../hooks/useOriginClickTrackingFunction';
import {StyledBorderedlinkText} from './ImageTeaserComponents/ImageCTA';
import {ImageTextline} from './ImageTeaserComponents/ImageTextline';
import {ImageTextsAndCTAWrapper} from './ImageTeaserComponents/ImageTextsAndCTAWrapper';
import {ImageWithHeadAndOverline} from './ImageTeaserComponents/ImageWithHeadAndOverline';
import {ImageWrapper} from './ImageTeaserComponents/ImageWrapper';

interface ImageSectionItemsProps {
  item: Flyout_ImageContainer_FlyoutSection_FlyoutSectionItem;
  className?: string;
  streamPosition: number;
  itemsAmount: number;
}

export const ImageSectionItem: React.FC<ImageSectionItemsProps> = ({
  item,
  streamPosition,
  itemsAmount,
}) => {
  const trackTeaserClick = useTrackFlyoutOriginClick();
  const handleTrackFlyoutContentItemClick = () => {
    trackTeaserClick();
  };
  switch (item.type?.$case) {
    case 'drops': {
      const dropTeaser = item.type.drops.imageTeaser;

      const trackingData =
        item.type.drops.type === DropType.LIVESHOW
          ? ({
              ...dropTeaser.promotionTracking,
              creativeName: 'teaser',
              locationId: 'liveshopping_flyout_teaser',
            } as PromotionTracker)
          : dropTeaser.promotionTracking;

      return (
        <TrackImageTeaser
          promotionTrackingData={trackingData}
          promoPath={dropTeaser.cta.url.url}
          streamPosition={streamPosition}
        >
          <ImageWithHeadAndOverline
            dataTestId={`dropTeaserLink-${streamPosition}`}
            url={dropTeaser.cta.url.url}
            onClick={handleTrackFlyoutContentItemClick}
          >
            <>
              <ImageTextsAndCTAWrapper
                shouldAddBiggerBottomPadding
                dataTestId={`dropTextsAndCTAWrapper_${streamPosition}`}
              >
                {dropTeaser.headline && (
                  <ImageTextline
                    dataTestId={`dropHeadline_${streamPosition}`}
                    textline={dropTeaser.headline}
                    upperCase={true}
                  />
                )}
                {dropTeaser.overline && (
                  <ImageTextline
                    dataTestId={`dropSubline_${streamPosition}`}
                    textline={dropTeaser.overline}
                    upperCase={true}
                  />
                )}
                {dropTeaser.cta.text && (
                  <StyledBorderedlinkText
                    data-testid={`dropCTA_${streamPosition}`}
                  >
                    <ImageTextline
                      dataTestId={`dropCTAText_${streamPosition}`}
                      textline={dropTeaser.cta.text}
                      shouldWrap={false}
                    />
                  </StyledBorderedlinkText>
                )}
              </ImageTextsAndCTAWrapper>
              <ImageWrapper
                imageTeaser={dropTeaser}
                dataTestId="dropTeaser"
                teaserType="drop"
              />
            </>
          </ImageWithHeadAndOverline>
        </TrackImageTeaser>
      );
    }

    case 'imageTeaser': {
      const imageTeaser = item.type.imageTeaser;

      let maxImageWidth: number | undefined;
      if (itemsAmount === 3) {
        // e.g. outfits (in Inspiration)
        maxImageWidth = 190;
      } else if (itemsAmount >= 5 && itemsAmount <= 8) {
        // e.g. all the clusters like sports or stories (in inspiration)
        maxImageWidth = 158;
      }

      // Teaser with only headline (normally the smaller grid-teasers like "sports")
      if (
        !imageTeaser.cta.text &&
        !imageTeaser.overline &&
        imageTeaser.headline
      ) {
        return (
          <TrackImageTeaser
            promotionTrackingData={imageTeaser.promotionTracking}
            promoPath={imageTeaser.cta.url.url}
            streamPosition={streamPosition}
          >
            <ImageWithHeadAndOverline
              dataTestId={`imageTeaserLink_${imageTeaser.cta.url.url}`}
              url={imageTeaser.cta.url.url}
            >
              <>
                <ImageTextsAndCTAWrapper
                  dataTestId={`imageTextsAndCTAWrapper_${streamPosition}`}
                >
                  <ImageTextline
                    dataTestId={`imageHeadline_${streamPosition}`}
                    textline={imageTeaser.headline}
                    upperCase={true}
                  />
                </ImageTextsAndCTAWrapper>
                <ImageWrapper
                  imageTeaser={imageTeaser}
                  maxImageWidth={maxImageWidth}
                  dataTestId="imageTeaser"
                />
              </>
            </ImageWithHeadAndOverline>
          </TrackImageTeaser>
        );
      }

      // Teasers with appearing subline (e.g. teasers in the "Inspiration"-flyout)
      if (
        !imageTeaser.cta.text &&
        imageTeaser.overline &&
        imageTeaser.headline
      ) {
        return (
          <TrackImageTeaser
            promotionTrackingData={imageTeaser.promotionTracking}
            promoPath={imageTeaser.cta.url.url}
            streamPosition={streamPosition}
          >
            <ImageWithHeadAndOverline
              dataTestId={`imageTeaserLink_${imageTeaser.cta.url.url}`}
              url={imageTeaser.cta.url.url}
            >
              <>
                <ImageTextsAndCTAWrapper
                  dataTestId={`dropsImageTextsAndCTAWrapper_${streamPosition}`}
                >
                  <ImageTextline
                    dataTestId={`imageHeadline_${streamPosition}`}
                    textline={imageTeaser.headline}
                    upperCase={true}
                  />
                  <ImageTextline
                    dataTestId={`imageSubline_${streamPosition}`}
                    textline={imageTeaser.overline}
                    upperCase={true}
                    shouldShowLineOnlyOnHover
                  />
                </ImageTextsAndCTAWrapper>
                <ImageWrapper
                  imageTeaser={imageTeaser}
                  maxImageWidth={maxImageWidth}
                  dataTestId="imageTeaser"
                />
              </>
            </ImageWithHeadAndOverline>
          </TrackImageTeaser>
        );
      }

      // Basic ImageTeaser with no texts over the image
      return (
        <TrackImageTeaser
          promotionTrackingData={imageTeaser.promotionTracking}
          promoPath={imageTeaser.cta.url.url}
          streamPosition={streamPosition}
        >
          <UnstyledLinkLinaria
            data-testid={`imageTeaserLink_${imageTeaser.cta.url.url}`}
            to={imageTeaser.cta.url.url}
          >
            <ImageWrapper
              imageTeaser={imageTeaser}
              maxImageWidth={maxImageWidth}
              dataTestId={`imageTeaser_${streamPosition}`}
            />
          </UnstyledLinkLinaria>
        </TrackImageTeaser>
      );
    }
    // Currently all former "brandTeasers" are returned as "imageTeasers" from the BE
    case 'brandTeaser':
    default:
      return null;
  }
};
